import React, { useState, FC } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { PolyFit } from "models/PolyFit";
import { FullAssessment } from "hooks/useFullAssessment";
import { Column } from "components/data-table/DataTable";
import { Step } from "../../utils/api";

interface Props {
  assessment: FullAssessment;
  columns: Column[];
  annotationSteps?: Step[];
  regression?: PolyFit;
  interpolatedStep: Step | null;
}

const ExportButtons: FC<Props> = ({ assessment, columns, annotationSteps, regression, interpolatedStep }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  let assessmentTable = "";
  let assessmentOneLine = "";
  let ltsOneLine = "";
  let interpolatedOneLine = "";
  let headings = "";

  const regressionCoefficients = regression?.coefficients.join("\t");

  const handleCopyToClipboard = async (text: string) => {
    const type = "text/plain";
    const clipboardItemData = {
      [type]: text,
    };
    const clipboardItem = new ClipboardItem(clipboardItemData);
    await navigator.clipboard.write([clipboardItem]);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  columns.forEach((column) => {
    const value = column.name;
    if (value) {
      headings += value;
    }
    headings += "\t";
  });

  assessment.steps.forEach((step) => {
    let retval = "";
    columns.forEach((column) => {
      const value = step[column.id];
      if (value) {
        retval += value;
      }
      retval += "\t";
    });

    assessmentTable += retval.slice(0, -1) + "\n";
    assessmentOneLine += retval;
  });

  if (annotationSteps) {
    annotationSteps.forEach((step) => {
      let retval = "";
      columns.forEach((column) => {
        const value = step[column.id];
        if (value) {
          retval += value;
        }
        retval += "\t";
      });

      assessmentTable += retval.slice(0, -1) + "\n";
      assessmentOneLine += retval;
      ltsOneLine += retval;
    });
  }

  if (interpolatedStep) {
    columns.forEach((column) => {
      const value = interpolatedStep[column.id];
      if (value) {
        interpolatedOneLine += value;
      }
      interpolatedOneLine += "\t";
    });
  }

  assessmentTable = assessmentTable.slice(0, -1);
  assessmentOneLine = assessmentOneLine.slice(0, -1);
  ltsOneLine = ltsOneLine.slice(0, -1);
  headings = headings.slice(0, -1);

  return (
    <Box>
      <Typography color="primary">
        Export to Clipboard
        {copySuccess && (
          <Typography style={{ color: "#2e9e46" }} display="inline">
            {" "}
            copied!
          </Typography>
        )}
      </Typography>
      <Button
        color="primary"
        variant="outlined"
        style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
        onClick={() => handleCopyToClipboard(headings)}
      >
        Headings
      </Button>
      <Button
        color="primary"
        variant="outlined"
        style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
        onClick={() => handleCopyToClipboard(assessmentTable)}
      >
        as Tablex
      </Button>

      <Button
        color="primary"
        variant="outlined"
        style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
        onClick={() => handleCopyToClipboard(assessmentOneLine)}
      >
        as One Line
      </Button>

      <Button
        color="primary"
        variant="outlined"
        style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
        disabled={!regressionCoefficients}
        onClick={() => handleCopyToClipboard(regressionCoefficients ?? "")}
      >
        Regression Coefficients
      </Button>

      {annotationSteps && (
        <Button
          color="primary"
          variant="outlined"
          style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
          onClick={() => handleCopyToClipboard(ltsOneLine)}
        >
          {"only LT's"}
        </Button>
      )}
      {interpolatedStep && (
        <Button
          color="primary"
          variant="outlined"
          style={{ marginRight: "0.5rem", marginBottom: "0.5rem" }}
          disabled={!/\S/.test(interpolatedOneLine)}
          onClick={() => handleCopyToClipboard(interpolatedOneLine)}
        >
          {"only DE"}
        </Button>
      )}
    </Box>
  );
};

export default ExportButtons;
